import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faPlus, faEdit, faTrash, faCheckCircle, faTimesCircle, faSpinner, faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import { adminUsersAPI } from '../../services/adminAPI';
import { toast } from 'react-toastify';
import './admin.css';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterActive, setFilterActive] = useState('all');
  const [pagination, setPagination] = useState({
    current_page: 1,
    total: 0,
    pages: 1
  });

  const [formData, setFormData] = useState({
    email: '',
    username: '',
    full_name: '',
    password: '',
    user_type: 'free',
    is_active: true
  });

  useEffect(() => {
    fetchUsers();
  }, [searchTerm, filterType, filterActive, pagination.current_page]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current_page,
        per_page: 50,
        search: searchTerm || undefined,
        user_type: filterType !== 'all' ? filterType : undefined,
        is_active: filterActive !== 'all' ? filterActive : undefined
      };

      const response = await adminUsersAPI.getAll(params);
      setUsers(response.data.users);
      setPagination({
        current_page: response.data.current_page,
        total: response.data.total,
        pages: response.data.pages
      });
    } catch (error) {
      toast.error('Failed to fetch users');
      console.error('Fetch users error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setModalMode('create');
    setFormData({
      email: '',
      username: '',
      full_name: '',
      password: '',
      user_type: 'free',
      is_active: true
    });
    setShowModal(true);
  };

  const handleEdit = (user) => {
    setModalMode('edit');
    setSelectedUser(user);
    setFormData({
      email: user.email,
      username: user.username,
      full_name: user.full_name,
      user_type: user.user_type,
      is_active: user.is_active,
      password: '' // Don't prefill password
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (modalMode === 'create') {
        await adminUsersAPI.create(formData);
        toast.success('User created successfully');
      } else {
        const updateData = { ...formData };
        if (!updateData.password) {
          delete updateData.password; // Don't update password if not provided
        }
        await adminUsersAPI.update(selectedUser.id, updateData);
        toast.success('User updated successfully');
      }

      setShowModal(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (user) => {
    try {
      if (user.is_active) {
        await adminUsersAPI.deactivate(user.id);
        toast.success('User deactivated successfully');
      } else {
        await adminUsersAPI.activate(user.id);
        toast.success('User activated successfully');
      }
      fetchUsers();
    } catch (error) {
      toast.error('Failed to toggle user status');
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Are you sure you want to delete user ${user.email}?`)) {
      return;
    }

    try {
      await adminUsersAPI.delete(user.id);
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>
          <FontAwesomeIcon icon={faUsers} />
          User Management
        </h1>
        <p>Manage all users in the system</p>
      </div>

      <div className="admin-card">
        <div className="admin-card-header">
          <h3 className="admin-card-title">All Users ({pagination.total})</h3>
          <button className="admin-btn-sm admin-btn-primary" onClick={handleCreate}>
            <FontAwesomeIcon icon={faPlus} />
            Create User
          </button>
        </div>

        <div className="admin-filter-bar">
          <div style={{ position: 'relative', flex: 1, minWidth: '250px' }}>
            <FontAwesomeIcon 
              icon={faSearch} 
              style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--admin-text-muted)' }}
            />
            <input
              type="text"
              className="admin-search-input"
              placeholder="Search by email, username, or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ paddingLeft: '2.5rem' }}
            />
          </div>

          <select
            className="admin-select"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="all">All Types</option>
            <option value="free">Free</option>
            <option value="premium">Premium</option>
            <option value="enterprise">Enterprise</option>
          </select>

          <select
            className="admin-select"
            value={filterActive}
            onChange={(e) => setFilterActive(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        </div>

        {loading ? (
          <div className="admin-loading">
            <FontAwesomeIcon icon={faSpinner} spin />
            <p>Loading users...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="admin-empty-state">
            <FontAwesomeIcon icon={faUsers} />
            <h3>No Users Found</h3>
            <p>Try adjusting your search or filters</p>
          </div>
        ) : (
          <>
            <div className="admin-table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Email</th>
                    <th>Username</th>
                    <th>Full Name</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td data-label="ID">{user.id}</td>
                      <td data-label="Email">{user.email}</td>
                      <td data-label="Username">{user.username}</td>
                      <td data-label="Full Name">{user.full_name}</td>
                      <td data-label="Type">
                        <span className={`admin-badge ${
                          user.user_type === 'enterprise' ? 'success' :
                          user.user_type === 'premium' ? 'primary' : 'warning'
                        }`}>
                          {user.user_type}
                        </span>
                      </td>
                      <td data-label="Status">
                        <span className={`admin-badge ${user.is_active ? 'success' : 'inactive'}`}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td data-label="Created">{new Date(user.created_at).toLocaleDateString()}</td>
                      <td data-label="Actions">
                        <div className="admin-actions">
                          <button
                            className="admin-btn-sm admin-btn-primary"
                            onClick={() => handleEdit(user)}
                            title="Edit"
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                          <button
                            className={`admin-btn-sm ${user.is_active ? 'admin-btn-danger' : 'admin-btn-success'}`}
                            onClick={() => handleToggleActive(user)}
                            title={user.is_active ? 'Deactivate' : 'Activate'}
                          >
                            <FontAwesomeIcon icon={user.is_active ? faTimesCircle : faCheckCircle} />
                          </button>
                          <button
                            className="admin-btn-sm admin-btn-danger"
                            onClick={() => handleDelete(user)}
                            title="Delete"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.pages > 1 && (
              <div className="admin-pagination">
                <button
                  disabled={pagination.current_page === 1}
                  onClick={() => setPagination({...pagination, current_page: pagination.current_page - 1})}
                >
                  Previous
                </button>
                <span style={{ color: 'var(--admin-text)' }}>
                  Page {pagination.current_page} of {pagination.pages}
                </span>
                <button
                  disabled={pagination.current_page === pagination.pages}
                  onClick={() => setPagination({...pagination, current_page: pagination.current_page + 1})}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="admin-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2 className="admin-modal-title">
                {modalMode === 'create' ? 'Create New User' : 'Edit User'}
              </h2>
              <button className="admin-modal-close" onClick={() => setShowModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="admin-form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                />
              </div>

              <div className="admin-form-group">
                <label>Username</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({...formData, username: e.target.value})}
                  required
                />
              </div>

              <div className="admin-form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                  required
                />
              </div>

              <div className="admin-form-group">
                <label>Password {modalMode === 'edit' && '(leave blank to keep current)'}</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  required={modalMode === 'create'}
                />
              </div>

              <div className="admin-form-group">
                <label>User Type</label>
                <select
                  value={formData.user_type}
                  onChange={(e) => setFormData({...formData, user_type: e.target.value})}
                >
                  <option value="free">Free</option>
                  <option value="premium">Premium</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>

              <div className="admin-form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    style={{ width: 'auto', marginRight: '0.5rem' }}
                  />
                  Active
                </label>
              </div>

              <div className="admin-modal-footer">
                <button
                  type="button"
                  className="admin-btn-sm admin-btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="admin-btn-sm admin-btn-primary" disabled={loading}>
                  {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : (modalMode === 'create' ? 'Create' : 'Update')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
